import { useState, useEffect } from 'react';
import { IAdresse } from 'components/AddressTypeahead/types';
import { EnhetViewModel } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import { KontonrOgFakturaAdresseSjekkResponse } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import styles from './index.module.css';
import AdresseInput from './AdresseInput';
import useToaster from 'utils/hooks/useToaster';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import {
  DeleteAddressCommand,
  UpdateAddresseCommand,
} from 'services/KjederegisteretAdminApi/EnhetApi/Commands';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import { Coordinates } from 'models/Coordinates';
import { UpdatePositionCommand } from 'services/KjederegisteretAdminApi/AdresseApi/types';
import AdresseApi from 'services/KjederegisteretAdminApi/AdresseApi';
import { AddresseTyper } from './types';
import EnhetKildeApi from 'services/KjederegisteretAdminApi/EnhetKildeApi';
import { KildeDetaljerViewModel } from 'services/KjederegisteretAdminApi/EnhetKildeApi/types';
import AddressSourcesPopover from './AddressSourcesPopover';

// Builds a synthetic "address" from a linked kilde's raw fields - never a real Adresse row,
// just enough shape (gateadresse/postnr/poststed/coordinates) to save via the normal address
// update command once the user picks it from AddressSourcesPopover.
const buildKildeAdresse = (kilde: KildeDetaljerViewModel): IAdresse | undefined => {
  const gateadresse = kilde.fields?.gateadresse;
  const poststed = kilde.fields?.poststed;
  if (!gateadresse || !poststed) return undefined;

  const postnr = kilde.fields?.postnummer;
  const latitude = kilde.fields?.latitude ? parseFloat(kilde.fields.latitude) : undefined;
  const longitude = kilde.fields?.longitude ? parseFloat(kilde.fields.longitude) : undefined;

  return {
    gateadresse,
    postnr,
    poststed,
    landkode: 'NO',
    latitude: isNaN(latitude) ? undefined : latitude,
    longitude: isNaN(longitude) ? undefined : longitude,
    formattedForOneLine: [gateadresse, [postnr, poststed].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', '),
  };
};

interface Props {
  enhet: EnhetViewModel;
  kontonrOgFakturaAdresseSjekkResponse: KontonrOgFakturaAdresseSjekkResponse;
  onEnhetUpdated: () => void;
}

const AdresserV2 = (props: Props) => {
  const toaster = useToaster();

  const [kildeDetaljer, setKildeDetaljer] = useState<KildeDetaljerViewModel[]>([]);
  useEffect(() => {
    let cancelled = false;
    EnhetKildeApi.getKildeDetaljer(props.enhet.id)
      .then((result) => {
        if (!cancelled) setKildeDetaljer(result);
      })
      .catch(() => {
        // Best-effort - "Bruk ...-adresse" from a kilde simply won't show up if this fails
      });
    return () => {
      cancelled = true;
    };
    // props.enhet (not just .id) so a re-fetch after linking/unlinking a kilde elsewhere (which
    // refreshes the Enhet via onEnhetUpdated, producing a new props.enhet reference) is actually
    // picked up - .id alone never changes on a same-Enhet refresh.
  }, [props.enhet]);

  const kildeAddressCandidates = kildeDetaljer
    .map((kilde) => {
      const adresse = buildKildeAdresse(kilde);
      return adresse ? { kilde: kilde.kilde, eksternId: kilde.eksternId, adresse } : undefined;
    })
    .filter((x): x is { kilde: string; eksternId: string; adresse: IAdresse } => !!x);

  const [besoksAdresse, setBesokssadresse] = useState<IAdresse>(props.enhet?.besoksadresse);
  const [besokEkstraInfo, setBesokEkstraInfo] = useState<string>(
    props.enhet?.besoksadresse?.ekstraInfo,
  );
  const [leveringsAdresse, setLeveringsadresse] = useState<IAdresse>(props.enhet?.leveringsadresse);
  const [leveringsEkstraInfo, setLeveringsEkstraInfo] = useState<string>(
    props.enhet?.leveringsadresse?.ekstraInfo,
  );
  const [postAdresse, setPostadresse] = useState<IAdresse>(props.enhet?.postadresse);
  const [postEkstraInfo, setPostEkstraInfo] = useState<string>(
    props.enhet?.postadresse?.ekstraInfo,
  );
  const [fakturaAdresse, setFakturaadresse] = useState<IAdresse>(props.enhet?.fakturaAdresse);
  const [fakturaEkstraInfo, setFakturaEkstraInfo] = useState<string>(
    props.enhet?.fakturaAdresse?.ekstraInfo,
  );

  // Track pending address removals (cleared but not saved)
  const [pendingBesokRemoval, setPendingBesokRemoval] = useState<boolean>(false);
  const [pendingLeveringRemoval, setPendingLeveringRemoval] = useState<boolean>(false);
  const [pendingPostRemoval, setPendingPostRemoval] = useState<boolean>(false);
  const [pendingFakturaRemoval, setPendingFakturaRemoval] = useState<boolean>(false);

  // Track when address was saved from "Bruk..." button to reset touched state
  const [resetBesokTouched, setResetBesokTouched] = useState<boolean>(false);
  const [resetLeveringTouched, setResetLeveringTouched] = useState<boolean>(false);
  const [resetPostTouched, setResetPostTouched] = useState<boolean>(false);
  const [resetFakturaTouched, setResetFakturaTouched] = useState<boolean>(false);

  // Track original address state to determine if address was previously set
  const hadBesoksAdresse = props.enhet?.besoksadresse?.id != null;
  const hadLeveringsAdresse = props.enhet?.leveringsadresse?.id != null;
  const hadPostAdresse = props.enhet?.postadresse?.id != null;
  const hadFakturaAdresse = props.enhet?.fakturaAdresse?.id != null;

  // states for coordinates
  const [besokCoordinates, setBesokCoordinates] = useState<Coordinates | null>(
    besoksAdresse?.latitude && besoksAdresse?.longitude
      ? new Coordinates(besoksAdresse?.latitude, besoksAdresse?.longitude)
      : null,
  );
  const [leveringCoordinates, setLeveringCoordinates] = useState<Coordinates | null>(
    leveringsAdresse?.latitude && leveringsAdresse?.longitude
      ? new Coordinates(leveringsAdresse?.latitude, leveringsAdresse?.longitude)
      : null,
  );
  const [postCoordinates, setPostCoordinates] = useState<Coordinates | null>(
    postAdresse?.latitude && postAdresse?.longitude
      ? new Coordinates(postAdresse?.latitude, postAdresse?.longitude)
      : null,
  );
  const [fakturaCoordinates, setFakturaCoordinates] = useState<Coordinates | null>(
    fakturaAdresse?.latitude && fakturaAdresse?.longitude
      ? new Coordinates(fakturaAdresse?.latitude, fakturaAdresse?.longitude)
      : null,
  );

  const handleSaveAddress = async (
    addressType: string,
    addressOverride?: IAdresse,
    ekstraInfoOverride?: string,
  ) => {
    const id = props.enhet.id;
    let updateAddressCommand: UpdateAddresseCommand;

    // Helper to build address object from either override or state
    const buildAddress = (adresse: IAdresse, ekstraInfo: string) => {
      if (!adresse) return null;
      return {
        gateadresse: adresse.gateadresse,
        postnr: adresse.postnr,
        poststed: adresse.poststed,
        landkode: adresse.landkode,
        koordinater:
          adresse.latitude && adresse.longitude
            ? new Coordinates(adresse.latitude, adresse.longitude)
            : null,
        ekstraInfo: ekstraInfo,
        erPostboksAdresse: false,
      };
    };

    let address = null;
    switch (addressType) {
      case 'besoksadresse':
        address = buildAddress(
          addressOverride ?? besoksAdresse,
          ekstraInfoOverride ?? besokEkstraInfo,
        );
        break;
      case 'leveringsadresse':
        address = buildAddress(
          addressOverride ?? leveringsAdresse,
          ekstraInfoOverride ?? leveringsEkstraInfo,
        );
        break;
      case 'postadresse':
        address = buildAddress(
          addressOverride ?? postAdresse,
          ekstraInfoOverride ?? postEkstraInfo,
        );
        break;
      case 'fakturaAdresse':
        address = buildAddress(
          addressOverride ?? fakturaAdresse,
          ekstraInfoOverride ?? fakturaEkstraInfo,
        );
        break;
      default:
        toaster.error('Feil!', 'Ugyldig adressetype.');
        return;
    }

    updateAddressCommand = {
      id: id,
      addressType: addressType,
      address: address,
    };

    try {
      await EnhetApi.updateAddress(id, updateAddressCommand);
      toaster.success('Suksess!', 'Adresse er oppdatert.');
      props.onEnhetUpdated();
    } catch (err) {
      toaster.error('Feil! Kunne ikke oppdatere adresse.', HttpErrorHelper.formatError(err));
    }
  };

  const handleSaveCoordinates = async (addressType: string, lat: number, lng: number) => {
    let updateCoordinatesCommand: UpdatePositionCommand;
    switch (addressType) {
      case 'besoksadresse':
        updateCoordinatesCommand = {
          id: besoksAdresse.id,
          lat,
          lng,
        };
        break;
      case 'leveringsadresse':
        updateCoordinatesCommand = {
          id: leveringsAdresse.id,
          lat,
          lng,
        };
        break;
      case 'postadresse':
        updateCoordinatesCommand = {
          id: postAdresse.id,
          lat,
          lng,
        };
        break;
      case 'fakturaAdresse':
        updateCoordinatesCommand = {
          id: fakturaAdresse.id,
          lat,
          lng,
        };
        break;
      default:
        toaster.error('Feil!', 'Ugyldig adressetype.');
        return;
    }
    try {
      await AdresseApi.updatePosition(updateCoordinatesCommand);
      toaster.success('Suksess!', 'Koordinater er oppdatert.');
      // props.onEnhetUpdated();
    } catch (err) {
      toaster.error('Feil! Kunne ikke oppdatere koordinater.', HttpErrorHelper.formatError(err));
    }
  };

  const handleDeleteCoordinates = async (addressType: string, id: number) => {
    let prevState;
    switch (addressType) {
      case 'besoksadresse':
        id = besoksAdresse.id;
        prevState = besokCoordinates;
        setBesokCoordinates(null);
        break;
      case 'leveringsadresse':
        id = leveringsAdresse.id;
        prevState = leveringCoordinates;
        setLeveringCoordinates(null);
        break;
      case 'postadresse':
        id = postAdresse.id;
        prevState = postCoordinates;
        setPostCoordinates(null);
        break;
      case 'fakturaAdresse':
        id = fakturaAdresse.id;
        prevState = fakturaCoordinates;
        setFakturaCoordinates(null);
        break;
      default:
        toaster.error('Feil!', 'Ugyldig adressetype.');
        return;
    }
    try {
      await AdresseApi.deletePosition(id);
      toaster.success('Suksess!', 'Koordinater er fjernet.');
      props.onEnhetUpdated();
    } catch (err) {
      switch (addressType) {
        case 'besoksadresse':
          setBesokCoordinates(prevState);
          break;
        case 'leveringsadresse':
          setLeveringCoordinates(prevState);
          break;
        case 'postadresse':
          setPostCoordinates(prevState);
          break;
        case 'fakturaAdresse':
          setFakturaCoordinates(prevState);
          break;
      }
      toaster.error('Feil! Kunne ikke slette koordinater.', HttpErrorHelper.formatError(err));
    }
  };

  // Clear address from view without calling API - marks as pending removal
  const handleClearAddress = (addressType: string) => {
    switch (addressType) {
      case 'besoksadresse':
        setBesokssadresse(null);
        setBesokEkstraInfo(null);
        setBesokCoordinates(null);
        setPendingBesokRemoval(true);
        break;
      case 'leveringsadresse':
        setLeveringsadresse(null);
        setLeveringsEkstraInfo(null);
        setLeveringCoordinates(null);
        setPendingLeveringRemoval(true);
        break;
      case 'postadresse':
        setPostadresse(null);
        setPostEkstraInfo(null);
        setPostCoordinates(null);
        setPendingPostRemoval(true);
        break;
      case 'fakturaAdresse':
        setFakturaadresse(null);
        setFakturaEkstraInfo(null);
        setFakturaCoordinates(null);
        setPendingFakturaRemoval(true);
        break;
    }
  };

  const handleDeleteAddress = async (addressType: string) => {
    const id = props.enhet.id;
    let deleteAddressCommand: DeleteAddressCommand;

    switch (addressType) {
      case 'besoksadresse':
        deleteAddressCommand = {
          id: id,
          addressType: addressType,
        };
        break;
      case 'leveringsadresse':
        deleteAddressCommand = {
          id: id,
          addressType: addressType,
        };
        break;
      case 'postadresse':
        deleteAddressCommand = {
          id: id,
          addressType: addressType,
        };
        break;
      case 'fakturaAdresse':
        deleteAddressCommand = {
          id: id,
          addressType: addressType,
        };
        break;
      default:
        toaster.error('Feil!', 'Ugyldig adressetype.');
        return;
    }

    try {
      await EnhetApi.deleteAddress(id, deleteAddressCommand);
      toaster.success('Suksess!', 'Adresse er fjernet.');
      // Reset pending removal state
      switch (addressType) {
        case 'besoksadresse':
          setPendingBesokRemoval(false);
          break;
        case 'leveringsadresse':
          setPendingLeveringRemoval(false);
          break;
        case 'postadresse':
          setPendingPostRemoval(false);
          break;
        case 'fakturaAdresse':
          setPendingFakturaRemoval(false);
          break;
      }
      props.onEnhetUpdated();
    } catch (err) {
      toaster.error('Feil! Kunne ikke slette adresse.', HttpErrorHelper.formatError(err));
    }
  };

  // Check if save should be disabled for a specific address type
  // When enhet is published and originally had an address, save is disabled until a new address is selected
  const isSaveDisabledForPendingRemoval = (addressType: string): boolean => {
    if (!props.enhet.erPublisert) return false;

    switch (addressType) {
      case 'besoksadresse':
        return pendingBesokRemoval && hadBesoksAdresse && !besoksAdresse;
      case 'leveringsadresse':
        return pendingLeveringRemoval && hadLeveringsAdresse && !leveringsAdresse;
      case 'postadresse':
        return pendingPostRemoval && hadPostAdresse && !postAdresse;
      case 'fakturaAdresse':
        return pendingFakturaRemoval && hadFakturaAdresse && !fakturaAdresse;
      default:
        return false;
    }
  };

  /* useEffect(() => {
        if (props.enhet.erPublisert && !besoksAdresse) {
        } else if (besokError || postError || fakturaError || leveringError) {

        } else {
        }
    }, [besoksAdresse, leveringsAdresse, postAdresse, fakturaAdresse, besokError, postError, fakturaError, leveringError]);
 */
  useEffect(() => {
    if (props.enhet?.besoksadresse?.formattedForOneLine !== besoksAdresse?.formattedForOneLine) {
      setBesokssadresse(props.enhet?.besoksadresse);
    }
    if (props.enhet?.besoksadresse?.ekstraInfo !== besokEkstraInfo) {
      setBesokEkstraInfo(props.enhet?.besoksadresse?.ekstraInfo);
    }
    if (
      props.enhet?.leveringsadresse?.formattedForOneLine !== leveringsAdresse?.formattedForOneLine
    ) {
      setLeveringsadresse(props.enhet?.leveringsadresse);
    }
    if (props.enhet?.leveringsadresse?.ekstraInfo !== leveringsEkstraInfo) {
      setLeveringsEkstraInfo(props.enhet?.leveringsadresse?.ekstraInfo);
    }
    if (props.enhet?.postadresse?.formattedForOneLine !== postAdresse?.formattedForOneLine) {
      setPostadresse(props.enhet?.postadresse);
    }

    if (props.enhet?.postadresse?.ekstraInfo !== postEkstraInfo) {
      setPostEkstraInfo(props.enhet?.postadresse?.ekstraInfo);
    }
    if (props.enhet?.fakturaAdresse?.formattedForOneLine !== fakturaAdresse?.formattedForOneLine) {
      setFakturaadresse(props.enhet?.fakturaAdresse);
    }

    if (props.enhet?.fakturaAdresse?.ekstraInfo !== fakturaEkstraInfo) {
      setFakturaEkstraInfo(props.enhet?.fakturaAdresse?.ekstraInfo);
    }

    // coordinates
    if (
      props.enhet?.besoksadresse?.latitude !== besokCoordinates?.lat ||
      props.enhet?.besoksadresse?.longitude !== besokCoordinates?.lng
    ) {
      if (
        props.enhet?.besoksadresse?.latitude != null &&
        props.enhet?.besoksadresse?.longitude != null
      ) {
        setBesokCoordinates(
          new Coordinates(props.enhet.besoksadresse.latitude, props.enhet.besoksadresse.longitude),
        );
      }
      setBesokssadresse(props.enhet?.besoksadresse);
    }
    if (
      props.enhet?.leveringsadresse?.latitude !== leveringCoordinates?.lat ||
      props.enhet?.leveringsadresse?.longitude !== leveringCoordinates?.lng
    ) {
      if (
        props.enhet?.leveringsadresse?.latitude != null &&
        props.enhet?.leveringsadresse?.longitude != null
      ) {
        setLeveringCoordinates(
          new Coordinates(
            props.enhet.leveringsadresse.latitude,
            props.enhet.leveringsadresse.longitude,
          ),
        );
        setLeveringsadresse(props.enhet?.leveringsadresse);
      }
    }

    if (
      props.enhet?.postadresse?.latitude !== postCoordinates?.lat ||
      props.enhet?.postadresse?.longitude !== postCoordinates?.lng
    ) {
      if (
        props.enhet?.postadresse?.latitude != null &&
        props.enhet?.postadresse?.longitude != null
      ) {
        setPostCoordinates(
          new Coordinates(props.enhet.postadresse.latitude, props.enhet.postadresse.longitude),
        );
        setPostadresse(props.enhet?.postadresse);
      }
    }

    if (
      props.enhet?.fakturaAdresse?.latitude !== fakturaCoordinates?.lat ||
      props.enhet?.fakturaAdresse?.longitude !== fakturaCoordinates?.lng
    ) {
      if (
        props.enhet?.fakturaAdresse?.latitude != null &&
        props.enhet?.fakturaAdresse?.longitude != null
      ) {
        setFakturaCoordinates(
          new Coordinates(
            props.enhet.fakturaAdresse.latitude,
            props.enhet.fakturaAdresse.longitude,
          ),
        );
        setFakturaadresse(props.enhet?.fakturaAdresse);
      }
    }
  }, [props.enhet]);

  const handleAddressCopied = async (
    adresse: IAdresse,
    ekstraInfo: string,
    type: AddresseTyper,
  ) => {
    let addressType: string;
    let setResetTouched: (value: boolean) => void;
    switch (type) {
      case 'Besøksadresse':
        setBesokssadresse(adresse);
        setBesokEkstraInfo(ekstraInfo);
        setPendingBesokRemoval(false);
        setResetTouched = setResetBesokTouched;
        addressType = 'besoksadresse';
        break;
      case 'Leveringsadresse':
        setLeveringsadresse(adresse);
        setLeveringsEkstraInfo(ekstraInfo);
        setPendingLeveringRemoval(false);
        setResetTouched = setResetLeveringTouched;
        addressType = 'leveringsadresse';
        break;
      case 'Postadresse':
        setPostadresse(adresse);
        setPostEkstraInfo(ekstraInfo);
        setPendingPostRemoval(false);
        setResetTouched = setResetPostTouched;
        addressType = 'postadresse';
        break;
      case 'FakturaAdresse':
        setFakturaadresse(adresse);
        setFakturaEkstraInfo(ekstraInfo);
        setPendingFakturaRemoval(false);
        setResetTouched = setResetFakturaTouched;
        addressType = 'fakturaAdresse';
        break;
    }
    // Automatically save the address after copying - pass address directly to avoid state timing issues
    await handleSaveAddress(addressType, adresse, ekstraInfo);
    // Signal to AdresseInput to reset touched state
    setResetTouched(true);
    // Reset the flag after a short delay so it can be triggered again
    setTimeout(() => setResetTouched(false), 100);
  };

  return (
    <div className={styles.container}>
      <AdresseInput
        title={
          <>
            Besøksadresse
            <AddressSourcesPopover
              currentAdresse={besoksAdresse}
              candidates={kildeAddressCandidates}
              onUseAddress={(adresse) => handleAddressCopied(adresse, undefined, 'Besøksadresse')}
            />
          </>
        }
        adresse={besoksAdresse}
        onAdresseChanged={(addr) => {
          setBesokssadresse(addr);
          if (addr) setPendingBesokRemoval(false);
        }}
        onAdresseCopied={(address, ekstraInfo) =>
          handleAddressCopied(address, ekstraInfo, 'Besøksadresse')
        }
        ekstraInfo={besokEkstraInfo}
        onEkstraInfoChanged={setBesokEkstraInfo}
        copyAddresses={[
          {
            adresse: leveringsAdresse,
            title: 'Bruk leveringsadresse',
            ekstraInfo: leveringsEkstraInfo,
          },
          { adresse: postAdresse, title: 'Bruk postadresse', ekstraInfo: postEkstraInfo },
          { adresse: fakturaAdresse, title: 'Bruk fakturaadresse', ekstraInfo: fakturaEkstraInfo },
        ]}
        requireCoordinates={true}
        onSave={() => handleSaveAddress('besoksadresse')}
        onClear={() => handleClearAddress('besoksadresse')}
        onDelete={() => handleDeleteAddress('besoksadresse')}
        saveDisabledDueToPendingRemoval={isSaveDisabledForPendingRemoval('besoksadresse')}
        resetTouchedState={resetBesokTouched}
        onUpdateCoordinates={({ lat, lng }) => handleSaveCoordinates('besoksadresse', lat, lng)}
        onDeleteCoordinates={(id) => handleDeleteCoordinates('besoksadresse', id)}
        coordinates={besokCoordinates}
        setCoordinates={(newCoordinates) => setBesokCoordinates(newCoordinates)}
      />

      <AdresseInput
        title="Leveringsadresse"
        adresse={leveringsAdresse}
        onAdresseChanged={(addr) => {
          setLeveringsadresse(addr);
          if (addr) setPendingLeveringRemoval(false);
        }}
        onAdresseCopied={(address, ekstraInfo) =>
          handleAddressCopied(address, ekstraInfo, 'Leveringsadresse')
        }
        ekstraInfo={leveringsEkstraInfo}
        onEkstraInfoChanged={setLeveringsEkstraInfo}
        copyAddresses={[
          { adresse: besoksAdresse, title: 'Bruk besøksadresse', ekstraInfo: besokEkstraInfo },
          { adresse: postAdresse, title: 'Bruk postadresse', ekstraInfo: postEkstraInfo },
          { adresse: fakturaAdresse, title: 'Bruk fakturaadresse', ekstraInfo: fakturaEkstraInfo },
        ]}
        requireCoordinates={true}
        onSave={() => handleSaveAddress('leveringsadresse')}
        onClear={() => handleClearAddress('leveringsadresse')}
        onDelete={() => handleDeleteAddress('leveringsadresse')}
        saveDisabledDueToPendingRemoval={isSaveDisabledForPendingRemoval('leveringsadresse')}
        resetTouchedState={resetLeveringTouched}
        onUpdateCoordinates={({ lat, lng }) => {
          handleSaveCoordinates('leveringsadresse', lat, lng);
        }}
        onDeleteCoordinates={(id) => handleDeleteCoordinates('leveringsadresse', id)}
        coordinates={leveringCoordinates}
        setCoordinates={(newCoordinates) => setLeveringCoordinates(newCoordinates)}
      />

      <AdresseInput
        title="Postadresse"
        adresse={postAdresse}
        onAdresseChanged={(addr) => {
          setPostadresse(addr);
          if (addr) setPendingPostRemoval(false);
        }}
        onAdresseCopied={(address, ekstraInfo) =>
          handleAddressCopied(address, ekstraInfo, 'Postadresse')
        }
        ekstraInfo={postEkstraInfo}
        onEkstraInfoChanged={setPostEkstraInfo}
        copyAddresses={[
          { adresse: besoksAdresse, title: 'Bruk besøksadresse', ekstraInfo: besokEkstraInfo },
          {
            adresse: leveringsAdresse,
            title: 'Bruk leveringsadresse',
            ekstraInfo: leveringsEkstraInfo,
          },
          { adresse: fakturaAdresse, title: 'Bruk fakturaadresse', ekstraInfo: fakturaEkstraInfo },
        ]}
        onSave={() => handleSaveAddress('postadresse')}
        onClear={() => handleClearAddress('postadresse')}
        onDelete={() => handleDeleteAddress('postadresse')}
        saveDisabledDueToPendingRemoval={isSaveDisabledForPendingRemoval('postadresse')}
        resetTouchedState={resetPostTouched}
        onUpdateCoordinates={({ lat, lng }) => {
          handleSaveCoordinates('postadresse', lat, lng);
        }}
        onDeleteCoordinates={(id) => handleDeleteCoordinates('postadresse', id)}
        coordinates={postCoordinates}
        setCoordinates={(newCoordinates) => setPostCoordinates(newCoordinates)}
      />

      <AdresseInput
        title="Fakturaadresse"
        adresse={fakturaAdresse}
        onAdresseChanged={(addr) => {
          setFakturaadresse(addr);
          if (addr) setPendingFakturaRemoval(false);
        }}
        onAdresseCopied={(address, ekstraInfo) =>
          handleAddressCopied(address, ekstraInfo, 'FakturaAdresse')
        }
        ekstraInfo={fakturaEkstraInfo}
        onEkstraInfoChanged={setFakturaEkstraInfo}
        copyAddresses={[
          { adresse: besoksAdresse, title: 'Bruk besøksadresse', ekstraInfo: besokEkstraInfo },
          {
            adresse: leveringsAdresse,
            title: 'Bruk leveringsadresse',
            ekstraInfo: leveringsEkstraInfo,
          },
          { adresse: postAdresse, title: 'Bruk postadresse', ekstraInfo: postEkstraInfo },
        ]}
        kontonrOgFakturaAdresseSjekkResponse={props.kontonrOgFakturaAdresseSjekkResponse}
        onSave={() => handleSaveAddress('fakturaAdresse')}
        onClear={() => handleClearAddress('fakturaAdresse')}
        onDelete={() => handleDeleteAddress('fakturaAdresse')}
        saveDisabledDueToPendingRemoval={isSaveDisabledForPendingRemoval('fakturaAdresse')}
        resetTouchedState={resetFakturaTouched}
        onUpdateCoordinates={({ lat, lng }) => {
          handleSaveCoordinates('fakturaAdresse', lat, lng);
        }}
        onDeleteCoordinates={(id) => handleDeleteCoordinates('fakturaAdresse', id)}
        coordinates={fakturaCoordinates}
        setCoordinates={(newCoordinates) => setFakturaCoordinates(newCoordinates)}
      />
    </div>
  );
};
export default AdresserV2;
